import type { Mermaid } from 'mermaid';
import { useEffect, useState } from 'react';
import { useTheme, type Theme } from '../lib/theme';

type Rendered = { svg: string } | { error: string };

/** Rendered diagrams by theme and source, so a re-mount (such as the print view) shows a diagram at once. */
const rendered = new Map<string, Rendered>();
const CACHE_LIMIT = 50;
let loader: Promise<Mermaid> | undefined;
let sequence = 0;
/** Mermaid's configuration is global, so renders run one at a time, each with its own theme. */
let queue: Promise<unknown> = Promise.resolve();

const cacheKey = (code: string, theme: Theme) => `${theme}\n${code}`;

/** Mermaid is large, so it loads only when a document contains a diagram. */
function loadMermaid() {
  loader ??= import('mermaid').then(({ default: mermaid }) => mermaid);
  return loader;
}

function configure(mermaid: Mermaid, theme: Theme) {
  mermaid.initialize({
    startOnLoad: false,
    // Encodes HTML in labels and disables click handlers and links.
    securityLevel: 'strict',
    theme: theme === 'dark' ? 'dark' : 'neutral',
    fontFamily: 'Inter, system-ui, sans-serif',
  });
}

/** Any CSS `url()` that does not point into the document itself. */
const REMOTE_URL = /url\(\s*(?!['"]?#)/i;

/**
 * Keeps diagrams from fetching anything (AC-27): image shapes, external references and CSS `url()`s other
 * than in-document ones (markers, gradients) are removed. Mermaid's strict mode already sanitizes markup.
 */
export function stripRemoteContent(svg: string): string {
  const doc = new DOMParser().parseFromString(svg, 'text/html');
  const root = doc.body.querySelector('svg');
  if (!root) throw new Error('The diagram did not render to SVG.');
  root
    .querySelectorAll('image, img, script, iframe, object, embed, audio, video')
    .forEach((el) => el.remove());
  for (const el of [root, ...root.querySelectorAll('*')]) {
    for (const { name, value } of [...el.attributes]) {
      const isRef = name === 'href' || name === 'xlink:href' || name === 'src';
      if ((isRef && !value.startsWith('#')) || REMOTE_URL.test(value)) el.removeAttribute(name);
    }
  }
  root.querySelectorAll('style').forEach((style) => {
    style.textContent = (style.textContent ?? '')
      .replace(/@import[^;]*;?/gi, '')
      .replace(/url\(\s*(?!['"]?#)[^)]*\)/gi, 'none');
  });
  return root.outerHTML;
}

/** Renders (or returns the cached rendering of) one diagram. Syntax errors are results, not exceptions. */
export async function renderMermaid(code: string, theme: Theme = 'light'): Promise<Rendered> {
  const key = cacheKey(code, theme);
  const hit = rendered.get(key);
  if (hit) return hit;
  const run = queue.then(async (): Promise<Rendered> => {
    try {
      const mermaid = await loadMermaid();
      configure(mermaid, theme);
      // parse() first: render() leaves an error graphic in the page when the syntax is wrong.
      await mermaid.parse(code);
      const { svg } = await mermaid.render(`mermaid-diagram-${++sequence}`, code);
      return { svg: stripRemoteContent(svg) };
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err) };
    }
  });
  queue = run;
  const result = await run;
  if (rendered.size >= CACHE_LIMIT) rendered.delete(rendered.keys().next().value!);
  rendered.set(key, result);
  return result;
}

/** A ```mermaid code block. Shows the source until the diagram is ready, and with the error if it fails. */
export function MermaidDiagram({ code }: { code: string }) {
  const theme = useTheme();
  const [, rerender] = useState(0);
  const result = rendered.get(cacheKey(code, theme));
  useEffect(() => {
    if (rendered.has(cacheKey(code, theme))) return;
    let live = true;
    void renderMermaid(code, theme).then(() => live && rerender((n) => n + 1));
    return () => {
      live = false;
    };
  }, [code, theme]);

  if (result && 'svg' in result)
    return (
      <figure
        className="mermaid-diagram"
        aria-label="Diagram"
        dangerouslySetInnerHTML={{ __html: result.svg }}
      />
    );
  return (
    <div className={result ? 'mermaid-failed' : 'mermaid-pending'} aria-busy={!result || undefined}>
      <pre>
        <code>{code}</code>
      </pre>
      {result && <p className="small muted">Diagram could not be rendered: {result.error}</p>}
    </div>
  );
}
